import { forwardRef, useId } from 'react';
import type { ComponentPropsWithoutRef, ForwardedRef } from 'react';
import { styled } from '@tmsw/powerups.config.stitches';
import { SrOnly } from '@tmsw/powerups.ui.a11y.sr-only';
import { ButtonIcon } from '@tmsw/powerups.ui.button-icon';
import './button.css';

// TODO: what is a variant anyway
enum ButtonVariants {
  flat = 'flat',
  gradient = 'gradient',
}

enum ButtonSizes {
  default = 'default',
  max = 'max',
  sm = "sm",
  md = "md",
  lg = "lg",
}

enum ButtonColors {
  primary = 'primary',
  secondary = 'secondary',
}

enum ButtonShapes {
  default = 'default',
  circle = 'circle',
  // square = 'square',
  pill = 'pill',
}

export type ButtonProps = {
  size?: any;
  color?: string | "primary" | "secondary" | any;
  shape?: ButtonShapes | any;
  iconRight?: boolean;
  ariaLabel?: string;
  autoArialLabeledBy?: boolean;
  variant?: ButtonVariants;
  icon?: string;
  css?: {
    [key: string]: unknown
  },
  // rest
  [key: string]: unknown
  // onClick?: MouseEventHandler<HTMLButtonElement>;
} & Omit<ComponentPropsWithoutRef<"button">, 'type'>;

export const defaultProps = {
  variant: ButtonVariants.flat,
  color: ButtonColors.primary,
  size: ButtonSizes.default,
  iconRight: false,
  ariaLabel: '',
  autoArialLabeledBy: false,
  css: {},
};

const StyledButton = styled('button', {
  '-moz-appearance': 'none',
  '-webkit-appearance': 'none',
  '-webkit-tap-highlight-color': 'transparent',
  display: 'inline-block',
  willChange: 'transform',
  transform: 'translate3d(0, 0, 0)',
  transition: 'background 250ms ease-in-out, transform 150ms ease',
  transformOrigin: 'center',
  margin: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  padding: 0,
  textAlign: 'center',
  textDecoration: 'none',
  color: 'initial',
  pointerEvents: 'auto',
  userSelect: 'none',
  '& > img': {
    width: '100%',
    height: '100%',
    display: 'block',
  },
  '& > svg': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transformOrigin: 'center',
  },
  variants: {
    color: {
      primary: {
        color: 'primary',
        '& > svg': {
          fill: '$colors.primary'
        },
      },
      secondary: {
        color: '$colors.secondary',
      },
    },
    size: {
      default: {
        width: 'auto',
      },
      max: {
        width: 'max-content',
      },
      sm: {},
      md: {},
      lg: {}
    },
    shape: {
      default: {},
      circle: {
        borderRadius: '50%',
      },
      square: {
        '&::before': {
          position: 'relative',
          display: 'block',
          content: '',
          width: '100%',
          paddingTop: '100%',
        }
      },
      pill: {
        borderRadius: '0.75rem',
      }
    }
  },
  compoundVariants: [
    {
      size: 'sm',
      shape: 'default' || 'pill',
      css: {
        padding: 1,
        fontSize: 1,
      }
    },
    {
      size: 'md',
      shape: 'default' || 'pill',
      css: {
        padding: 2,
        fontSize: 2,
      }
    },
    {
      size: 'lg',
      shape: 'default' || 'pill',
      css: {
        padding: 3,
        fontSize: 3,
      }
    },
    {
      size: 'sm',
      shape: 'circle' || 'square',
      css: {
        width: `vars(--circle-button-size-sm, 10vmin)`,
        height: `vars(--circle-button-size-sm, 10vmin)`,
      }
    },
    {
      size: 'md',
      shape: 'circle' || 'square',
      css: {
        width: `vars(--circle-button-size-md, 12vmin)`,
        height: `vars(--circle-button-size-md, 12vmin)`,
      }
    },
    {
      size: 'lg',
      shape: 'circle' || 'square',
      css: {
        width: `vars(--circle-button-size-lg, 14vmin)`,
        height: `vars(--circle-button-size-lg, 14vmin)`,
      }
    }
  ]
});

//
export const Button = forwardRef((props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {

  const labelId = useId();
  const { icon, iconRight, ariaLabel, autoArialLabeledBy, variant, shape, color, children, ...rest } = props;

  return (
    <StyledButton
      type="button"
      color={color}
      ref={ref}
      data-variant={variant}
      data-shape={shape}
      data-color={color}
      data-icon={icon}
      {...(!autoArialLabeledBy ? { 'aria-label': ariaLabel } : {})}
      {...(autoArialLabeledBy ? { 'aria-labelledby': labelId } : {})}
      {...rest}
    >
      {autoArialLabeledBy && <SrOnly as="span" id={labelId}>{ariaLabel}</SrOnly>}
      {icon && <ButtonIcon shape={shape} icon={icon} iconRight={iconRight} />}
      {children}
    </StyledButton>
  );
});

Button.defaultProps = defaultProps;

export default Button;